import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import IpeAuthModal from '@/components/ipe/IpeAuthModal';
import IpeLoginShowcase from '@/components/ipe/IpeLoginShowcase';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { IPE_WORKBENCH_PATH, isSafeWorkbenchRedirect } from '@/config/ipeRoutes';
import { fundingAuthAPI, getFundingToken } from '@/utils/api';
import { IpeBrandMark } from '@/components/ipe/IpeBrandLogo';
import { showToast } from '@/utils/toast';
import {
  isIpePluginEmbed,
  isIpePluginSilentCheck,
  notifyIpePluginAuthSuccess,
} from '@/utils/ipePluginBridge';
import IpeLocaleSwitcher from '@/i18n/IpeLocaleSwitcher';
import '@/styles/ipe/ipe-auth.css';

const TEAM_INVITE_STORAGE_KEY = 'ipe_pending_team_invite';
const TEAM_INVITE_CODE_STORAGE_KEY = 'ipe_pending_team_invite_code';

function IpeHeaderBrand() {
  return (
    <IpeBrandMark
      className="ipe-login-page__brand"
      textClassName="ipe-login-page__brand-text"
      text="IPE"
    />
  );
}

export default function IpeLoginPage() {
  const { t } = useTranslation('login');
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated,
    bootstrapping,
    login,
    loginBySms,
    loginSubmitting,
    register,
    registerSubmitting,
    resetPassword,
    sendVerificationCode,
    completeWeChatLogin,
  } = useIpeAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [resetPasswordSubmitting, setResetPasswordSubmitting] = useState(false);

  const teamInviteToken = useMemo(() => {
    const fromUrl = new URLSearchParams(location.search).get('teamInvite');
    if (fromUrl) {
      sessionStorage.setItem(TEAM_INVITE_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(TEAM_INVITE_STORAGE_KEY) || '';
  }, [location.search]);

  const teamInviteCode = useMemo(() => {
    const fromUrl = new URLSearchParams(location.search).get('teamInviteCode');
    if (fromUrl) {
      sessionStorage.setItem(TEAM_INVITE_CODE_STORAGE_KEY, fromUrl.trim().toUpperCase());
      return fromUrl.trim().toUpperCase();
    }
    return sessionStorage.getItem(TEAM_INVITE_CODE_STORAGE_KEY) || '';
  }, [location.search]);

  const isPluginEmbed = useMemo(() => isIpePluginEmbed(), [location.search]);
  const isSilentCheck = useMemo(() => isIpePluginSilentCheck(), [location.search]);

  const redirectAfterLogin = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('redirect');
    if (isSafeWorkbenchRedirect(raw)) return raw;
    return IPE_WORKBENCH_PATH;
  }, [location.search]);

  const acceptPendingInvite = useCallback(async () => {
    const token = sessionStorage.getItem(TEAM_INVITE_STORAGE_KEY);
    if (token) {
      try {
        await fundingAuthAPI.acceptTeamInvite(token);
      } catch {
        // ignore — user may already be in team
      } finally {
        sessionStorage.removeItem(TEAM_INVITE_STORAGE_KEY);
      }
    }
    const code = sessionStorage.getItem(TEAM_INVITE_CODE_STORAGE_KEY);
    if (code) {
      try {
        await fundingAuthAPI.joinTeamByInviteCode(code);
      } catch {
        // ignore — user may already be in team
      } finally {
        sessionStorage.removeItem(TEAM_INVITE_CODE_STORAGE_KEY);
      }
    }
  }, []);

  const finishAuthSession = useCallback(async () => {
    if (!getFundingToken()) return false;
    await acceptPendingInvite();
    setAuthModalOpen(false);
    if (isPluginEmbed) {
      notifyIpePluginAuthSuccess();
      return true;
    }
    navigate(redirectAfterLogin, { replace: true });
    return true;
  }, [acceptPendingInvite, isPluginEmbed, navigate, redirectAfterLogin]);

  const handleRegister = useCallback(
    async (phone, password, nickname, code, loginContext = {}) => {
      const data = await register(phone, password, nickname, code, loginContext);
      await finishAuthSession();
      return data;
    },
    [finishAuthSession, register],
  );

  const handleLogin = useCallback(
    async (phone, password, loginContext = {}) => {
      await login(phone, password, loginContext);
      await finishAuthSession();
    },
    [finishAuthSession, login],
  );

  const handleSmsLogin = useCallback(
    async (phone, code, loginContext = {}, captchaToken) => {
      await loginBySms(phone, code, loginContext, captchaToken);
      await finishAuthSession();
    },
    [finishAuthSession, loginBySms],
  );

  const handleWeChatLogin = useCallback(
    async (token, loginContext = {}) => {
      await completeWeChatLogin(token, loginContext);
      await finishAuthSession();
    },
    [completeWeChatLogin, finishAuthSession],
  );

  const handleResetPassword = useCallback(async (phone, code, newPassword) => {
    setResetPasswordSubmitting(true);
    try {
      return await resetPassword(phone, code, newPassword);
    } finally {
      setResetPasswordSubmitting(false);
    }
  }, [resetPassword]);

  const openAuthModal = useCallback((mode = 'login') => {
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  }, []);

  const pageRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('wechat') === 'success') {
      showToast(t('header.wechatSuccess'), 'success', 2500);
    }
  }, [location.search, t]);

  useEffect(() => {
    const root = pageRef.current;
    if (!root) return undefined;

    let scrollEndTimer = null;
    let ticking = false;

    const syncScrollState = () => {
      root.dataset.scrolling = 'true';
      root.dataset.pastHero = window.scrollY > window.innerHeight * 0.72 ? 'true' : '';
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(syncScrollState);
      }

      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(() => {
        root.dataset.scrolling = '';
      }, 140);
    };

    syncScrollState();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(scrollEndTimer);
      root.dataset.scrolling = '';
      root.dataset.pastHero = '';
    };
  }, []);

  useEffect(() => {
    document.title = t('header.login');
    if (bootstrapping) return;
    if (isPluginEmbed && isSilentCheck && isAuthenticated) {
      notifyIpePluginAuthSuccess();
      return;
    }
    if (!isPluginEmbed && isAuthenticated) {
      void acceptPendingInvite().finally(() => {
        navigate(redirectAfterLogin, { replace: true });
      });
    }
  }, [acceptPendingInvite, bootstrapping, isAuthenticated, isPluginEmbed, isSilentCheck, navigate, redirectAfterLogin, t]);

  useEffect(() => {
    if (!isPluginEmbed || bootstrapping) return;
    if (isSilentCheck) return;
    setAuthModalMode('login');
    setAuthModalOpen(true);
  }, [bootstrapping, isPluginEmbed, isSilentCheck]);

  if (bootstrapping) {
    return (
      <div
        className={`ipe-login-page ipe-login-page--landing ipe-login-page--loading${isPluginEmbed ? ' ipe-login-page--plugin-embed' : ''}`}
      >
        <p className="ipe-login-page__loading-text">{t('header.loading')}</p>
      </div>
    );
  }

  if (isPluginEmbed) {
    return (
      <div className="ipe-login-page ipe-login-page--plugin-embed">
        {isSilentCheck ? (
          <p className="ipe-login-page__loading-text">{t('header.syncing')}</p>
        ) : (
          <IpeAuthModal
            open={authModalOpen}
            mode={authModalMode}
            onModeChange={setAuthModalMode}
            onClose={() => setAuthModalOpen(false)}
            onLoginSubmit={handleLogin}
            onSmsLoginSubmit={handleSmsLogin}
            onWeChatLoginSuccess={handleWeChatLogin}
            onRegisterSubmit={handleRegister}
            onResetPasswordSubmit={handleResetPassword}
            onSendVerificationCode={sendVerificationCode}
            loginSubmitting={loginSubmitting}
            registerSubmitting={registerSubmitting}
            resetPasswordSubmitting={resetPasswordSubmitting}
          />
        )}
      </div>
    );
  }

  return (
    <div ref={pageRef} className="ipe-login-page ipe-login-page--landing">
      <header className="ipe-login-page__header ipe-login-page__header--sticky">
        <IpeHeaderBrand />
        <nav className="ipe-login-page__nav-spacer" aria-hidden="true" />
        <div className="ipe-login-page__header-actions">
          <span data-ipe-locale-dock="">
            <IpeLocaleSwitcher />
          </span>
          <button
            type="button"
            className="ipe-login-page__header-btn ipe-login-page__header-btn--outline"
            onClick={() => openAuthModal('login')}
          >
            {t('header.login')}
          </button>
          <button
            type="button"
            className="ipe-login-page__header-btn ipe-login-page__header-btn--primary"
            onClick={() => openAuthModal('register')}
          >
            {t('header.register')}
          </button>
        </div>
      </header>

      <main className="ipe-login-page__scroll-main">
        <IpeLoginShowcase
          onOpenLogin={() => openAuthModal('login')}
        />
      </main>

      <IpeAuthModal
        open={authModalOpen}
        mode={authModalMode}
        onModeChange={setAuthModalMode}
        onClose={() => setAuthModalOpen(false)}
        onLoginSubmit={handleLogin}
        onSmsLoginSubmit={handleSmsLogin}
        onWeChatLoginSuccess={handleWeChatLogin}
        onRegisterSubmit={handleRegister}
        onResetPasswordSubmit={handleResetPassword}
        onSendVerificationCode={sendVerificationCode}
        loginSubmitting={loginSubmitting}
        registerSubmitting={registerSubmitting}
        resetPasswordSubmitting={resetPasswordSubmitting}
      />
    </div>
  );
}
